// src/components/admin/editors/HeroEditor.js
import React, { useState, useEffect } from "react";
import { db } from "../../../firebaseConfig/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Save, Upload, X, Image as ImageIcon, Video } from "lucide-react";
import { uploadImage, deleteImageByUrl } from "../../../utils/imageUtils";

const HeroEditor = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [heroData, setHeroData] = useState({
    name: "",
    title: "",
    backgroundType: "images", // 'images' o 'video'
    backgroundImages: ["", "", "", ""],
    videoUrl: "",
  });

  useEffect(() => {
    loadHeroData();
  }, []);

  const loadHeroData = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, "content", "hero");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setHeroData(docSnap.data());
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      setMessage({ type: "error", text: "Error al cargar los datos" });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setMessage({
        type: "info",
        text: "Convirtiendo a WebP y subiendo imagen...",
      });

      // Si ya existe una imagen en ese índice, eliminarla primero
      const oldImageUrl = heroData.backgroundImages[index];
      if (oldImageUrl) {
        await deleteImageByUrl(oldImageUrl);
      }

      // Subir nueva imagen (automáticamente convertida a WebP)
      const url = await uploadImage(file, `hero/background-${index}`);

      const newImages = [...heroData.backgroundImages];
      newImages[index] = url;
      setHeroData({ ...heroData, backgroundImages: newImages });
      setMessage({
        type: "success",
        text: "Imagen optimizada y subida correctamente (WebP 90%)",
      });
    } catch (error) {
      console.error("Error al subir imagen:", error);
      setMessage({ type: "error", text: "Error al subir la imagen" });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const docRef = doc(db, "content", "hero");
      await setDoc(docRef, heroData);
      setMessage({ type: "success", text: "Cambios guardados correctamente" });
    } catch (error) {
      console.error("Error al guardar:", error);
      setMessage({ type: "error", text: "Error al guardar los cambios" });
    } finally {
      setSaving(false);
    }
  };

  const removeImage = async (index) => {
    const imageUrl = heroData.backgroundImages[index];

    // Eliminar de Storage
    if (imageUrl) {
      try {
        await deleteImageByUrl(imageUrl);
        setMessage({ type: "success", text: "Imagen eliminada de Storage" });
      } catch (error) {
        console.error("Error al eliminar imagen:", error);
      }
    }

    // Actualizar estado
    const newImages = [...heroData.backgroundImages];
    newImages[index] = "";
    setHeroData({ ...heroData, backgroundImages: newImages });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Message */}
      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : message.type === "error"
                ? "bg-red-50 text-red-800 border border-red-200"
                : "bg-blue-50 text-blue-800 border border-blue-200"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* Información básica */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Información Principal
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre
              </label>
              <input
                type="text"
                value={heroData.name}
                onChange={(e) =>
                  setHeroData({ ...heroData, name: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tu nombre"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título / Profesión
              </label>
              <input
                type="text"
                value={heroData.title}
                onChange={(e) =>
                  setHeroData({ ...heroData, title: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Fotógrafo Audiovisual"
              />
            </div>
          </div>
        </div>

        {/* Tipo de fondo */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Fondo de la sección Hero
          </h3>

          <div className="flex space-x-4 mb-6">
            <button
              onClick={() =>
                setHeroData({ ...heroData, backgroundType: "images" })
              }
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border-2 transition-all ${
                heroData.backgroundType === "images"
                  ? "border-blue-600 bg-blue-50 text-blue-600"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <ImageIcon size={20} />
              <span>Carrusel de Imágenes</span>
            </button>
            <button
              onClick={() =>
                setHeroData({ ...heroData, backgroundType: "video" })
              }
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border-2 transition-all ${
                heroData.backgroundType === "video"
                  ? "border-blue-600 bg-blue-50 text-blue-600"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <Video size={20} />
              <span>Video de YouTube</span>
            </button>
          </div>

          {/* Carrusel de imágenes */}
          {heroData.backgroundType === "images" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Sube hasta 4 imágenes para el carrusel de fondo
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {heroData.backgroundImages.map((image, index) => (
                  <div
                    key={index}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-all"
                  >
                    {image ? (
                      <div className="relative">
                        <img
                          src={image}
                          alt={`Background ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-32 cursor-pointer">
                        <Upload className="text-gray-400 mb-2" size={32} />
                        <span className="text-sm text-gray-600">
                          Subir imagen {index + 1}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, index)}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video de YouTube */}
          {heroData.backgroundType === "video" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL del video de YouTube
                </label>
                <input
                  type="text"
                  value={heroData.videoUrl}
                  onChange={(e) =>
                    setHeroData({ ...heroData, videoUrl: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <p className="mt-2 text-sm text-gray-500">
                  Copia la URL completa del video de YouTube
                </p>
              </div>

              {heroData.videoUrl && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Vista previa:
                  </p>
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${heroData.videoUrl.split("v=")[1]?.split("&")[0]}`}
                      title="YouTube video preview"
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botón guardar */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            <span>{saving ? "Guardando..." : "Guardar cambios"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroEditor;
